import { useState, useEffect } from "react"
import { Alert, Button, Offcanvas, Form, Dropdown, DropdownButton, Accordion, ListGroup } from "react-bootstrap";

const DEVICE_TYPES = [
    { value: "cameras", label: "Cameras" },
    { value: "gondolas", label: "Gondolas" },
    { value: "gateways", label: "Gateways" },
    { value: "scales", label: "Scales" }
];

const RadioFilter = ({ label, name, value, onChange }) => (
    <Form.Group className="mt-3">
        <Form.Label>{label}: &nbsp;</Form.Label>
        <Form.Check inline label="Any" type="radio" name={name}
            checked={value === "-"} onChange={() => onChange("-")} />
        <Form.Check inline label="True" type="radio" name={name}
            checked={value === "true"} onChange={() => onChange("true")} />
        <Form.Check inline label="False" type="radio" name={name}
            checked={value === "false"} onChange={() => onChange("false")} />
    </Form.Group>
);

const cameraFiltersFromParams = (params) => ({
    lens: params.get("lens") ?? "-",
    roles: params.get("roles") ?? "-",
    mac_address: params.get("mac_address") ?? "",
    ip_address: params.get("ip_address") ?? "",
    backend_id: params.get("backend_id") ?? "",
    scanned: params.get("scanned") ?? "-",
    connection_check: params.get("connection_check") ?? "-",
    qa_done: params.get("qa_done") ?? "-",
    qa_check: params.get("qa_check") ?? "-",
    has_enough_calib_samples: params.get("has_enough_calib_samples") ?? "",
});

const gondolaFiltersFromParams = (params) => ({
    gondola_external_id: params.get("gondola_external_id") ?? "",
    gondola_backend_id: params.get("gondola_backend_id") ?? "",
    calibrated: params.get("calibrated") ?? "-",
});

const gatewayFiltersFromParams = (params) => ({
    gateway_backend_id: params.get("gateway_backend_id") ?? "",
    gateway_mac_address: params.get("gateway_mac_address") ?? "",
    gateway_ip_address: params.get("gateway_ip_address") ?? "",
    gateway_connected: params.get("gateway_connected") ?? "-",
    gateway_qa_state: params.get("gateway_qa_state") ?? "-",
});

const scaleFiltersFromParams = (params) => ({
    scale_serial_number: params.get("scale_serial_number") ?? "",
    scale_backend_id: params.get("scale_backend_id") ?? "",
    qa_factory_triangulation_min_average_error: params.get("qa_factory_triangulation_min_average_error") ?? "",
    qa_store_triangulation_min_average_error: params.get("qa_store_triangulation_min_average_error") ?? "",
    qa_factory_calibration_min_average_error: params.get("qa_factory_calibration_min_average_error") ?? "",
    scale_qa_check: params.get("scale_qa_check") ?? "-",
    scale_qa_state: params.get("scale_qa_state") ?? "-",
});

const SearchSideBar = (props) => {

    const { isVisible, hideSideBar, cameras, gondolas, searchParams, setSearchParams, selectedCameraId, setSelectedCameraId } = props;

    const [selectedDeviceType, setSelectedDeviceType] = useState(DEVICE_TYPES[0].value);
    const [cameraFilters, setCameraFilters] = useState(() => cameraFiltersFromParams(searchParams));
    const [gondolaFilters, setGondolaFilters] = useState(() => gondolaFiltersFromParams(searchParams));
    const [gatewayFilters, setGatewayFilters] = useState(() => gatewayFiltersFromParams(searchParams));
    const [scaleFilters, setScaleFilters] = useState(() => scaleFiltersFromParams(searchParams));

    useEffect(() => {
        const device = searchParams.get("device");
        if (device === "cameras") {
            setCameraFilters(cameraFiltersFromParams(searchParams));
            setSelectedDeviceType("cameras");
        } else if (device === "gondolas") {
            setGondolaFilters(gondolaFiltersFromParams(searchParams));
            setSelectedDeviceType("gondolas");
        } else if (device === "gateways") {
            setGatewayFilters(gatewayFiltersFromParams(searchParams));
            setSelectedDeviceType("gateways");
        } else if (device === "scales") {
            setScaleFilters(scaleFiltersFromParams(searchParams));
            setSelectedDeviceType("scales");
        } else {
            setCameraFilters(cameraFiltersFromParams(searchParams));
            setGondolaFilters(gondolaFiltersFromParams(searchParams));
            setGatewayFilters(gatewayFiltersFromParams(searchParams));
            setScaleFilters(scaleFiltersFromParams(searchParams));
            setSelectedDeviceType(DEVICE_TYPES[0].value);
        }
    }, [searchParams]);

    const cameraNumberDisplay = searchParams.get("device") === "cameras" ? cameras.filtered_camera_ids.length : cameras.total_cameras;
    const gondolaNumberDisplay = searchParams.get("device") === "gondolas" ? gondolas.total_filtered : gondolas.gondolas.length;

    const onSubmitFilters = (filters) => {
        const newParams = {};
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "" && v !== "-") {
                newParams[k] = v;
            }
        });
        if (Object.keys(newParams).length > 0) newParams.device = selectedDeviceType;
        setSearchParams(newParams);
        if (selectedDeviceType !== "cameras") hideSideBar();
    };

    const renderActiveFilterString = () => {
        return Array.from(searchParams).length === 0 ? "" : "('" + searchParams.toString().split('&').join(', ') + "')";
    }

    return (
        <Offcanvas show={isVisible} onHide={() => { hideSideBar() }} placement="start">
            <Offcanvas.Header closeButton>
                <img src="/sensei-small-logo.jpg" alt="small sensei logo"/>
                <Offcanvas.Title className="text-secondary fs-4 fw-bold">Search Devices</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header
                            className="text-wrap text-break"
                            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                            Filters {renderActiveFilterString()}
                        </Accordion.Header>
                        <Accordion.Body>
                            <DropdownButton
                                id="dropdown-basic-button"
                                title={DEVICE_TYPES.find((t) => t.value === selectedDeviceType).label}
                                style={{ width: '100%' }}
                                className="w-100 dropdown-custom mb-3"
                                selected={selectedDeviceType}
                                variant="outline-primary"
                                onSelect={(eventKey) => setSelectedDeviceType(eventKey)}
                            >
                                {DEVICE_TYPES.map((t) => (
                                    <Dropdown.Item key={t.value} eventKey={t.value}>{t.label}</Dropdown.Item>
                                ))}
                            </DropdownButton>

                            {selectedDeviceType === "cameras" &&
                                <Form noValidate onSubmit={(e) => { e.preventDefault(); onSubmitFilters(cameraFilters); }}>
                                    <Form.Group controlId="form.LensFilter">
                                        <Form.Label>Lens</Form.Label>
                                        <Form.Select size="sm" value={cameraFilters.lens}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, lens: e.target.value }))}>
                                            <option value="-">-</option>
                                            <option value="2.1">2.1</option>
                                            <option value="2.8">2.8</option>
                                            <option value="3.6">3.6</option>
                                            <option value="4.0">4.0</option>
                                            <option value="6.0">6.0</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.RolesFilter">
                                        <Form.Label>Roles</Form.Label>
                                        <Form.Select size="sm" value={cameraFilters.roles}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, roles: e.target.value }))}>
                                            <option value="-">-</option>
                                            <option value="person_tracking_top_view">Person Tracking Top View</option>
                                            <option value="person_tracking_side_view">Person Tracking Side View</option>
                                            <option value="top_view_shelf">Top View Shelf</option>
                                            <option value="shelf_monitoring">Shelf Monitoring</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.MacAddressFilter">
                                        <Form.Label>Mac Address</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<mac_address>"
                                            value={cameraFilters.mac_address}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, mac_address: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.IPAddressFilter">
                                        <Form.Label>IP Address</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<ip_address>"
                                            value={cameraFilters.ip_address}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, ip_address: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.BackendIdFilter">
                                        <Form.Label>Backend Id</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<backend_id>"
                                            value={cameraFilters.backend_id}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, backend_id: e.target.value }))} />
                                    </Form.Group>
                                    <RadioFilter label="Scanned" name="scanned"
                                        value={cameraFilters.scanned}
                                        onChange={(v) => setCameraFilters(p => ({ ...p, scanned: v }))} />
                                    <RadioFilter label="Connected" name="connection_check"
                                        value={cameraFilters.connection_check}
                                        onChange={(v) => setCameraFilters(p => ({ ...p, connection_check: v }))} />
                                    <RadioFilter label="QA Done" name="qa_done"
                                        value={cameraFilters.qa_done}
                                        onChange={(v) => setCameraFilters(p => ({ ...p, qa_done: v }))} />
                                    <RadioFilter label="QA Check" name="qa_check"
                                        value={cameraFilters.qa_check}
                                        onChange={(v) => setCameraFilters(p => ({ ...p, qa_check: v }))} />
                                    <Form.Group className="mt-3" controlId="form.HasEnoughCalibSamples">
                                        <Form.Label>Sampled: &nbsp;</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<sample_set_id>"
                                            value={cameraFilters.has_enough_calib_samples}
                                            onChange={(e) => setCameraFilters(p => ({ ...p, has_enough_calib_samples: e.target.value }))} />
                                    </Form.Group>
                                    <Button variant="dark" style={{ width: '100%' }} className="mt-3 mb-3" type="submit">
                                        Filter
                                    </Button>
                                </Form>
                            }

                            {selectedDeviceType === "gondolas" &&
                                <Form noValidate onSubmit={(e) => { e.preventDefault(); onSubmitFilters(gondolaFilters); }}>
                                    <Form.Group className="mt-3" controlId="form.ExternalId">
                                        <Form.Label>External Id</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<external_id>"
                                            value={gondolaFilters.gondola_external_id}
                                            onChange={(e) => setGondolaFilters(p => ({ ...p, gondola_external_id: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.BackendId">
                                        <Form.Label>Backend Id</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<backend_id>"
                                            value={gondolaFilters.gondola_backend_id}
                                            onChange={(e) => setGondolaFilters(p => ({ ...p, gondola_backend_id: e.target.value }))} />
                                    </Form.Group>
                                    <RadioFilter label="Calibrated" name="calibrated"
                                        value={gondolaFilters.calibrated}
                                        onChange={(v) => setGondolaFilters(p => ({ ...p, calibrated: v }))} />
                                    <Button variant="dark" style={{ width: '100%' }} className="mt-3 mb-3" type="submit">
                                        Filter
                                    </Button>
                                </Form>
                            }

                            {selectedDeviceType === "gateways" &&
                                <Form noValidate onSubmit={(e) => { e.preventDefault(); onSubmitFilters(gatewayFilters); }}>
                                    <Form.Group className="mt-3" controlId="form.GatewayBackendId">
                                        <Form.Label>Backend Id</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<backend_id>"
                                            value={gatewayFilters.gateway_backend_id}
                                            onChange={(e) => setGatewayFilters(p => ({ ...p, gateway_backend_id: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.GatewayMacAddress">
                                        <Form.Label>Mac Address</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<mac_address>"
                                            value={gatewayFilters.gateway_mac_address}
                                            onChange={(e) => setGatewayFilters(p => ({ ...p, gateway_mac_address: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.GatewayIpAddress">
                                        <Form.Label>IP Address</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<ip_address>"
                                            value={gatewayFilters.gateway_ip_address}
                                            onChange={(e) => setGatewayFilters(p => ({ ...p, gateway_ip_address: e.target.value }))} />
                                    </Form.Group>
                                    <RadioFilter label="Connected" name="gateway_connected"
                                        value={gatewayFilters.gateway_connected}
                                        onChange={(v) => setGatewayFilters(p => ({ ...p, gateway_connected: v }))} />
                                    <RadioFilter label="QA State" name="gateway_qa_state"
                                        value={gatewayFilters.gateway_qa_state}
                                        onChange={(v) => setGatewayFilters(p => ({ ...p, gateway_qa_state: v }))} />
                                    <Button variant="dark" style={{ width: '100%' }} className="mt-3 mb-3" type="submit">
                                        Filter
                                    </Button>
                                </Form>
                            }

                            {selectedDeviceType === "scales" &&
                                <Form noValidate onSubmit={(e) => { e.preventDefault(); onSubmitFilters(scaleFilters); }}>
                                    <Form.Group className="mt-3" controlId="form.SerialNumber">
                                        <Form.Label>Serial Number</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<serial_number>"
                                            value={scaleFilters.scale_serial_number}
                                            onChange={(e) => setScaleFilters(p => ({ ...p, scale_serial_number: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.ScaleBackendId">
                                        <Form.Label>Backend Id</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="<backend_id>"
                                            value={scaleFilters.scale_backend_id}
                                            onChange={(e) => setScaleFilters(p => ({ ...p, scale_backend_id: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.QAFactoryTriangulationMinAverageError">
                                        <Form.Label>QA Factory Triang. Error &ge;</Form.Label>
                                        <Form.Control size="sm" type="number" step="any" min="0" placeholder="<qa_factory_triangulation_min_average_error>"
                                            value={scaleFilters.qa_factory_triangulation_min_average_error}
                                            onChange={(e) => setScaleFilters(p => ({ ...p, qa_factory_triangulation_min_average_error: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.QAStoreTriangulationMinAverageError">
                                        <Form.Label>QA Store Triang. Error &ge;</Form.Label>
                                        <Form.Control size="sm" type="number" step="any" min="0" placeholder="<qa_store_triangulation_min_average_error>"
                                            value={scaleFilters.qa_store_triangulation_min_average_error}
                                            onChange={(e) => setScaleFilters(p => ({ ...p, qa_store_triangulation_min_average_error: e.target.value }))} />
                                    </Form.Group>
                                    <Form.Group className="mt-3" controlId="form.QAFactoryCalibrationMinAverageError">
                                        <Form.Label>QA Factory Calib. Error &ge;</Form.Label>
                                        <Form.Control size="sm" type="number" step="any" min="0" placeholder="<qa_factory_calibration_min_average_error>"
                                            value={scaleFilters.qa_factory_calibration_min_average_error}
                                            onChange={(e) => setScaleFilters(p => ({ ...p, qa_factory_calibration_min_average_error: e.target.value }))} />
                                    </Form.Group>
                                    <RadioFilter label="QA Check" name="scale_qa_check"
                                        value={scaleFilters.scale_qa_check}
                                        onChange={(v) => setScaleFilters(p => ({ ...p, scale_qa_check: v }))} />
                                    <RadioFilter label="QA State" name="scale_qa_state"
                                        value={scaleFilters.scale_qa_state}
                                        onChange={(v) => setScaleFilters(p => ({ ...p, scale_qa_state: v }))} />
                                    <Button variant="dark" style={{ width: '100%' }} className="mt-3 mb-3" type="submit">
                                        Filter
                                    </Button>
                                </Form>
                            }
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>Cameras - {cameraNumberDisplay} </Accordion.Header>
                        <Accordion.Body>
                            <ListGroup>
                                <ListGroup.Item> Total Cameras: {cameras.total_cameras}</ListGroup.Item>
                                <ListGroup.Item> Filtered Cameras: {cameras.filtered_camera_ids.length}</ListGroup.Item>
                            </ListGroup>
                            {cameras.total_cameras !== cameras.total_cameras_in_backend &&
                                <Alert variant="danger" className="mt-3">
                                    Inconsistent Camera Number: {cameras.total_cameras} in deployment and {cameras.total_cameras_in_backend} in database.
                                </Alert>}

                            <div className="mt-3">
                                <strong>Filtered Cameras List</strong>
                                {cameras.filtered_camera_ids.length === 0 ? (
                                    <Alert variant="secondary" className="mt-2 mb-0 py-2">
                                        No filtered cameras
                                    </Alert>
                                ) : (
                                    <ListGroup
                                        className="mt-2"
                                        style={{ maxHeight: "240px", overflowY: "auto" }}
                                    >
                                        {cameras.filtered_camera_ids.map((id) => (
                                            <ListGroup.Item
                                                key={id}
                                                action
                                                active={id === selectedCameraId}
                                                onClick={() => setSelectedCameraId(id === selectedCameraId ? null : id)}
                                            >
                                                {id}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>Gondolas - {gondolaNumberDisplay} </Accordion.Header>
                        <Accordion.Body>
                            <ListGroup>
                                <ListGroup.Item> Total Gondolas: {gondolas.gondolas.length}</ListGroup.Item>
                                <ListGroup.Item> Total Gateways: {gondolas.total_gateways}</ListGroup.Item>
                                <ListGroup.Item> Total Scales: {gondolas.total_scales}</ListGroup.Item>
                                <ListGroup.Item> Total Filtered: {gondolas.total_filtered}</ListGroup.Item>
                            </ListGroup>
                            {gondolas.gondolas.length !== gondolas.total_gondolas_in_backend &&
                                <Alert variant="danger" className="mt-3">
                                    Inconsistent Gondolas: {gondolas.gondolas.length} in deployment and {gondolas.total_gondolas_in_backend} in database.
                                </Alert>}
                            {gondolas.total_scales !== gondolas.total_scales_in_backend &&
                                <Alert variant="danger" className="mt-3">
                                    Inconsistent Scales: {gondolas.total_scales} in deployment and {gondolas.total_scales_in_backend} in database.
                                </Alert>}
                            {gondolas.total_gateways !== gondolas.total_gateways_in_backend &&
                                <Alert variant="danger" className="mt-3">
                                    Inconsistent Gateways: {gondolas.total_gateways} in deployment and {gondolas.total_gateways_in_backend} in database.
                                </Alert>}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default SearchSideBar;
